import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { AlertService } from '@marmon/frontend/alert';
import {
	DialogBase,
	DialogOptions,
	ENVIRONMENT,
	USER,
} from '@marmon/frontend/common';
import { ConfigApiService } from '@marmon/frontend/config';
import { ReportType, ReportTypes } from '@marmon/shared/types/report';
import { DefaultUserNotifications, isAdmin } from '@marmon/shared/types/user';
import { TuiButton, TuiLabel, TuiTitle } from '@taiga-ui/core';
import { TuiButtonLoading, TuiSwitch, TuiTabs } from '@taiga-ui/kit';
import { AuthService } from '../../services/auth.service';

/** Report type ids shown when config is missing (matches ReportTypes). */
const FALLBACK_REPORT_TYPE_IDS: ReportType[] = [
	ReportTypes.EightyTwenty,
	ReportTypes.QBR,
];

@Component({
	selector: 'mar-account-settings-dialog',
	standalone: true,
	imports: [
		TranslocoPipe,
		TuiTabs,
		TuiButton,
		TuiButtonLoading,
		ReactiveFormsModule,
		TuiSwitch,
		TuiTitle,
		TuiLabel,
	],
	templateUrl: 'account-settings-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsDialogComponent
	extends DialogBase<void>
	implements OnInit
{
	static override DialogOptions = {
		header: 'user.account-settings',
		dismissible: true,
		size: 'm',
	} as DialogOptions;

	readonly authService = inject(AuthService);
	private readonly userSubject = inject(USER);
	private readonly configApiService = inject(ConfigApiService);
	private readonly alert = inject(AlertService);
	private readonly environment = inject(ENVIRONMENT);

	readonly saving = signal(false);
	readonly digestLoading = signal(false);
	/** Report type ids from config (enabled only); used for digest toggles. */
	readonly reportTypeIds = signal<ReportType[]>([]);

	readonly form = new FormGroup({
		email: new FormControl<boolean>(
			this.userSubject.value?.notifications?.email ??
				DefaultUserNotifications().email,
			{ nonNullable: true },
		),
		reports: new FormGroup<Record<string, FormControl<boolean>>>({}),
	});

	activeTabIndex = 0;

	get user() {
		return this.userSubject.value;
	}

	get isAdmin() {
		return this.user != null && isAdmin(this.user);
	}

	get isDevelopEnvironment() {
		return !this.environment.production;
	}

	ngOnInit() {
		this.loadReportTypesConfig();
	}

	loadReportTypesConfig() {
		this.configApiService.getReportsConfig().then((config) => {
			const ids = config
				? (Object.keys(config).filter(
						(k) => config[k]?.enabled === true,
					) as ReportType[])
				: [...FALLBACK_REPORT_TYPE_IDS];
			this.reportTypeIds.set(
				(ids.length > 0 ? ids : [...FALLBACK_REPORT_TYPE_IDS]).sort(),
			);
			const reportsGroup = this.form.get('reports') as FormGroup;
			ids.forEach((id) => {
				if (!reportsGroup.contains(id)) {
					reportsGroup.addControl(
						id,
						new FormControl<boolean>(
							this.user?.notifications?.reports?.[id] ?? false,
							{ nonNullable: true },
						),
					);
				}
			});
		});
	}

	async onSave() {
		if (this.form.invalid || this.saving()) return;
		this.saving.set(true);
		try {
			const reportsValue = this.form.controls.reports.value ?? {};
			const reports =
				Object.keys(reportsValue).length > 0
					? Object.fromEntries(
							Object.entries(reportsValue).filter(
								(entry): entry is [string, boolean] =>
									entry[1] === true,
							),
						)
					: undefined;
			await this.authService.update({
				notifications: {
					email: this.form.controls.email.value,
					...(reports && Object.keys(reports).length > 0
						? { reports }
						: {}),
				},
			});
			this.alert.open('generic-success').subscribe();
		} catch (err) {
			console.error('AccountSettingsDialog.onSave', err);
			this.alert.open('generic-error').subscribe();
		} finally {
			this.saving.set(false);
		}
	}

	onCancel() {
		this.onDismiss();
	}

	getReportControl(reportTypeId: string): FormControl<boolean> | null {
		return this.form.controls.reports.get(
			reportTypeId,
		) as FormControl<boolean> | null;
	}
}
